import React, { useEffect, useRef, useCallback } from 'react';
import { MoreVertical, FileText } from 'lucide-react';

/**
 * GradeCellMenu - Kebab (⋮) menu rendered inside each grade cell.
 *
 * Props:
 *   studentId          – the student's ID for this row
 *   cellProjectId      – the project column this cell belongs to
 *   activeProjectId    – the project currently linked to the loaded rubric (may be null)
 *   hasRubricLoaded    – whether any rubric is loaded in the app
 *   onOpenGradingRubric – callback(studentId) invoked when the user clicks "Open grading rubric"
 *   openMenuKey        – the currently-open menu key (studentId_projectId) or null
 *   onMenuToggle       – callback(menuKey | null) to coordinate single-menu-open-at-a-time
 */
const GradeCellMenu = ({
    studentId,
    cellProjectId,
    activeProjectId,
    hasRubricLoaded,
    onOpenGradingRubric,
    openMenuKey,
    onMenuToggle
}) => {
    const menuRef = useRef(null);
    const buttonRef = useRef(null);
    const menuKey = `${studentId}_${cellProjectId}`;
    const isOpen = openMenuKey === menuKey;
    const isEnabled = hasRubricLoaded && activeProjectId === cellProjectId;

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (
                menuRef.current && !menuRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)
            ) {
                onMenuToggle(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onMenuToggle]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onMenuToggle(null);
                buttonRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onMenuToggle]);

    const handleToggle = useCallback((e) => {
        e.stopPropagation();
        if (!isEnabled) return;
        onMenuToggle(isOpen ? null : menuKey);
    }, [isEnabled, isOpen, menuKey, onMenuToggle]);

    const handleOpenRubric = useCallback((e) => {
        e.stopPropagation();
        onMenuToggle(null);
        onOpenGradingRubric(studentId);
    }, [onMenuToggle, onOpenGradingRubric, studentId]);

    return (
        <div className="absolute top-0.5 right-0.5 z-10">
            <button
                ref={buttonRef}
                onClick={handleToggle}
                disabled={!isEnabled}
                className={`p-0.5 rounded transition-colors ${
                    isEnabled
                        ? 'text-gray-400 hover:text-gray-700 hover:bg-gray-200 cursor-pointer'
                        : 'text-gray-300 cursor-not-allowed opacity-50'
                }`}
                title={isEnabled ? 'Grade cell options' : 'Load this assignment\'s rubric to enable'}
                aria-haspopup={isEnabled ? 'menu' : undefined}
                aria-expanded={isEnabled ? isOpen : undefined}
                aria-disabled={!isEnabled ? 'true' : undefined}
                aria-label="Grade cell menu"
            >
                <MoreVertical className="h-3 w-3" />
            </button>

            {isOpen && isEnabled && (
                <div
                    ref={menuRef}
                    role="menu"
                    className="absolute right-0 top-5 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48 text-left"
                >
                    <button
                        role="menuitem"
                        onClick={handleOpenRubric}
                        className="w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                        <FileText className="h-3 w-3 text-blue-500" />
                        Open grading rubric
                    </button>
                </div>
            )}
        </div>
    );
};

export default GradeCellMenu;
